import { apiRequest } from './client'
import {
  IdentityInfo,
  DomainResponse,
  VerifyDomainRequest,
  DeleteDomainResponse,
  ListDomainsResponse,
  ListDomainsParams,
  BulkVerifyRequest,
  BulkJobCreateResponse,
  BulkJobStatusResponse,
  BulkJobSummary,
  VerificationStatusResponse,
  ActionResult,
} from '@/lib/types'

export interface PaginatedDomainsResult {
  domains: ListDomainsResponse['domains']
  total: number
  nextCursor?: string
}

export async function listDomains(
  params: ListDomainsParams = {}
): Promise<ActionResult<PaginatedDomainsResult>> {
  const searchParams = new URLSearchParams()
  if (params.search) searchParams.set('search', params.search)
  if (params.status) searchParams.set('status', params.status)
  if (params.account_id) searchParams.set('account_id', params.account_id)
  if (params.limit) searchParams.set('limit', params.limit.toString())
  if (params.cursor) searchParams.set('cursor', params.cursor)

  const url = `/list${searchParams.toString() ? `?${searchParams}` : ''}`
  const result = await apiRequest<ListDomainsResponse>(url)

  if (result.success && result.data) {
    return {
      success: true,
      data: {
        domains: result.data.domains || [],
        total: result.data.total || 0,
        nextCursor: result.data.next_cursor,
      },
    }
  }
  return { success: false, error: result.error }
}

export async function getDomainInfo(domain: string): Promise<ActionResult<IdentityInfo>> {
  const result = await apiRequest<DomainResponse>(`/info?domain=${encodeURIComponent(domain)}`)

  if (result.success && result.data) {
    if (result.data.error) {
      return { success: false, error: result.data.error }
    }
    return { success: true, data: result.data.identity_info }
  }
  return { success: false, error: result.error }
}

export async function verifyDomain(
  request: VerifyDomainRequest
): Promise<ActionResult<IdentityInfo>> {
  const result = await apiRequest<DomainResponse>('/verify', {
    method: 'POST',
    body: JSON.stringify(request),
  })

  if (result.success && result.data) {
    if (result.data.error) {
      return { success: false, error: result.data.error }
    }
    return { success: true, data: result.data.identity_info }
  }
  return { success: false, error: result.error }
}

export async function deleteDomain(accountId: string, domain: string): Promise<ActionResult<DeleteDomainResponse>> {
  return apiRequest<DeleteDomainResponse>('/delete', {
    method: 'POST',
    body: JSON.stringify({ account_id: accountId, domain }),
  })
}

export async function createBulkJob(
  request: BulkVerifyRequest
): Promise<ActionResult<BulkJobCreateResponse>> {
  return apiRequest<BulkJobCreateResponse>('/domains/bulk', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export async function getBulkJobStatus(
  jobId: string
): Promise<ActionResult<BulkJobStatusResponse>> {
  return apiRequest<BulkJobStatusResponse>(`/domains/bulk/status?job_id=${encodeURIComponent(jobId)}`)
}

export async function listBulkJobs(): Promise<ActionResult<BulkJobSummary[]>> {
  const result = await apiRequest<{ jobs: BulkJobSummary[] }>('/domains/bulk/jobs')
  if (result.success && result.data) {
    return { success: true, data: result.data.jobs }
  }
  return { success: false, error: result.error }
}

export async function getVerificationStatus(
  accountId: string,
  domain: string
): Promise<ActionResult<VerificationStatusResponse>> {
  return apiRequest<VerificationStatusResponse>(
    `/verify/status?account_id=${encodeURIComponent(accountId)}&domain=${encodeURIComponent(domain)}`
  )
}

export async function startVerificationPolling(
  accountId: string,
  domain: string
): Promise<ActionResult<{ message: string; domain: string }>> {
  return apiRequest<{ message: string; domain: string }>('/verify/poll', {
    method: 'POST',
    body: JSON.stringify({ account_id: accountId, domain }),
  })
}
